import { useState } from "react";
import {
  Coins,
  Languages,
  Globe,
  ChevronDown,
  AlertTriangle,
  Check,
  Loader2,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { dictionary, Language } from "../../utils/dictionary";
import { useAuth } from "../../context/AuthContext";
import {
  upsertUserSettings,
  UserSettingsPayloadInterface,
} from "../../services/settings/userSettings";
import { handleApi } from "../../utils/handleApi";
import toast from "react-hot-toast";
import { getCountriesList, getLanguagesList } from "../../const/countryList";
import { useNavigate } from "react-router-dom";
import { decodeTokenService } from "../../services/decodeTokenService";
import { getSurveyValidateFormSurvey } from "../../services/survey/surveyService";

export function ConfigurationPanel() {
  const [step, setStep] = useState<"welcome" | "config">("welcome");
  const [country, setCountry] = useState<string>("");
  const { language, setLanguage } = useAuth();
  const [languagePreference, setLanguagePreference] =
    useState<Language>(language);
  const [currency, setCurrency] = useState("COP");
  const languagesList = getLanguagesList(language);
  const countries = getCountriesList(language);
  const [loadingCompleteConfiguration, setLoadingCompleteConfiguration] =
    useState(false);
  const navigate = useNavigate();

  const handleCountrySelect = (c: string) => {
    const selectedCountry = countries.find((item) => item.name === c);
    setCountry(c);

    if (selectedCountry) {
      setCurrency(selectedCountry.currency);
      const lang = languagesList.find(
        (l) => l.code === selectedCountry.language
      )?.name as Language;
      setLanguagePreference(lang);
    }
    setStep("config");
  };

  const handleLanguageChange = (val: string) => {
    const lang = languagesList.find((l) => l.code === val)?.name as Language;
    setLanguagePreference(lang);
  };

  const handleCompleteConfiguration = async () => {
    const countryCode = countries.find((c) => c.name === country)?.country;
    const languageCode = languagesList.find(
      (l) => l.name === languagePreference
    )?.code;

    if (!countryCode || !languageCode) return;

    setLoadingCompleteConfiguration(true);

    try {
      const data: UserSettingsPayloadInterface = {
        country: countryCode,
        language: languageCode,
        currency,
      };

      const { error } = await handleApi(upsertUserSettings(data), false);

      if (error) {
        toast.error(dictionary["error-completing-configuration"][language]);
        return;
      }

      toast.success(
        dictionary["configuration-completed-successfully"][language]
      );
      setLanguage(languageCode as Language);
      await checkSurvey();
    } finally {
      setLoadingCompleteConfiguration(false);
    }
  };

  const checkSurvey = async () => {
    const userId = decodeTokenService.getUserIdFromToken();
    if (!userId) return;
    const survey = await getSurveyValidateFormSurvey("default", "user", userId);
    if (survey.data.status !== "success") {
      navigate("/survey");
    } else {
      navigate("/home");
    }
  };

  const currentCountry = countries.find((c) => c.name === country);

  return (
    <div className="relative flex-shrink-0 w-full max-w-[525px] z-10 mx-auto">
      {/* Cards Container */}
      <div className="bg-white rounded-2xl sm:rounded-3xl lg:rounded-[40px] shadow-xl p-6 sm:p-8 md:p-10 lg:p-[54px] min-h-[320px] transition-all duration-300">
        {step === "welcome" ? (
          // --- Welcome Step ---
          <div className="flex flex-col gap-4 sm:gap-6 animate-in fade-in zoom-in duration-300">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="bg-[#DAE4FF]/50 p-2 sm:p-2.5 rounded-full flex-shrink-0">
                <Globe className="text-[#3E3EFF] w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <h2 className="text-xl sm:text-2xl md:text-[28px] lg:text-[34px] font-bold leading-tight text-[#0F172B] text-left">
                {dictionary["what-country-do-you-want-to"][language]} <br />
                <span
                  className="bg-clip-text text-transparent bg-gradient-to-r from-[#3E3EFF] to-[#26317F] block w-fit text-left"
                  style={{
                    WebkitTextFillColor: "transparent",
                    backgroundImage:
                      "linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0) 100%), linear-gradient(rgb(62, 62, 255) 0%, rgb(38, 49, 127) 100%)",
                  }}
                >
                  {dictionary["sell-with-fluxi"][language]}
                </span>
              </h2>
            </div>

            <div className="w-full mt-2 sm:mt-4">
              <Select onValueChange={handleCountrySelect}>
                <SelectTrigger className="w-full h-[50px] sm:h-[60px] rounded-full border border-[#26317F]/30 bg-white px-4 sm:px-6 text-base sm:text-lg text-[#6a6a6a] shadow-sm hover:border-[#3E3EFF] transition-colors focus:ring-2 focus:ring-[#3E3EFF]/20 flex items-center justify-between">
                  <SelectValue
                    placeholder={dictionary["select-your-country"][language]}
                  />
                  <ChevronDown className="ml-auto h-5 w-5 opacity-50" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px] overflow-y-auto rounded-[20px] bg-white border border-slate-100 shadow-xl p-2 z-[100]">
                  {countries.map((c) => (
                    <SelectItem
                      key={c.name}
                      value={c.name}
                      className="py-3 px-4 text-base cursor-pointer rounded-xl hover:bg-slate-50 transition-colors flex items-center"
                    >
                      {c.icon && (
                        <c.icon width="24" height="24" className="mr-3" />
                      )}
                      {/* <span className="mr-3 text-xl">{c.flag}</span>{" "} */}
                      <span className="text-slate-700">{c.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : (
          // --- Config Step ---
          <div className="flex flex-col gap-4 sm:gap-6 animate-in slide-in-from-right duration-300">
            <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
              <div className="bg-[#DAE4FF]/50 p-1.5 sm:p-2 rounded-xl flex-shrink-0">
                <Globe className="text-[#3E3EFF] w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-medium text-[#0F172B]">
                {
                  dictionary["what-country-do-you-want-to-sell-with-fluxi"][
                    language
                  ]
                }
              </h3>
            </div>

            {/* Country Display (Interactive to change back) */}
            <div className="relative w-full">
              <Select value={country} onValueChange={handleCountrySelect}>
                <SelectTrigger className="w-full h-[50px] sm:h-[60px] rounded-full border border-[#26317F]/30 bg-white px-4 sm:px-6 font-medium text-sm sm:text-base text-slate-900 shadow-sm flex items-center justify-between">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    {/* <span className="text-xl">{currentFlag}</span> */}
                    {currentCountry?.icon && (
                      <currentCountry.icon
                        width="20"
                        height="20"
                        className="sm:w-6 sm:h-6 flex-shrink-0"
                      />
                    )}
                    <span className="truncate">{country}</span>
                  </div>
                  <ChevronDown className="ml-auto h-4 w-4 sm:h-5 sm:w-5 opacity-50 flex-shrink-0" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px] overflow-y-auto rounded-[20px] bg-white border border-slate-100 shadow-xl p-2 z-[100]">
                  {countries.map((c) => (
                    <SelectItem
                      key={c.name}
                      value={c.name}
                      className="py-3 px-4 text-base cursor-pointer rounded-xl hover:bg-slate-50 transition-colors flex items-center"
                    >
                      {c.icon && (
                        <c.icon width="24" height="24" className="mr-3" />
                      )}
                      <span className="text-slate-700">{c.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="w-full flex justify-center py-2">
              <div className="h-[1px] bg-[#3E3EFF] w-[150px]"></div>
            </div>

            {/* Info Box */}
            <div className="bg-[#F5F8FF] border border-[#3E3EFF] rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-start sm:items-center gap-2 sm:gap-3">
              <AlertTriangle className="text-[#3E3EFF] w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5 sm:mt-0" />
              <p className="text-[#3E3EFF] text-[10px] sm:text-xs font-semibold leading-tight text-left">
                {dictionary["the-currency-is-linked-to-your-country"][language]}{" "}
                {
                  dictionary["you-can-change-the-language-if-you-need"][
                    language
                  ]
                }
              </p>
            </div>

            {/* Currency */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="bg-[#DAE4FF]/50 p-1.5 sm:p-2 rounded-xl flex-shrink-0">
                  <Coins className="text-[#3E3EFF] w-3 h-3" />
                </div>
                <span className="font-medium text-[#0F172B] text-base sm:text-lg">
                  {dictionary["currency"][language]}
                </span>
              </div>
              <div className="text-left sm:text-right">
                <p className="font-medium text-slate-900 text-xs sm:text-sm">
                  {currency} -{" "}
                  {currency === "COP"
                    ? dictionary["colombian-peso"][language]
                    : currency === "USD"
                    ? dictionary["dollar"][language]
                    : dictionary["local-currency"][language]}{" "}
                  ($)
                </p>
                <span className="inline-block bg-[#E7FFF3] text-[#00A650] text-[10px] sm:text-[11px] font-bold px-2 py-0.5 rounded mt-1">
                  {dictionary["linked-to-the-country"][language]}
                </span>
              </div>
            </div>

            {/* Language */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="bg-[#DAE4FF]/50 p-1.5 sm:p-2 rounded-xl flex-shrink-0">
                  <Languages className="text-[#3E3EFF] w-3 h-3" />
                </div>
                <span className="font-medium text-[#0F172B] text-base sm:text-lg">
                  {dictionary["language"][language]}
                </span>
              </div>
              <div className="w-full sm:w-[280px]">
                <Select
                  value={languagePreference}
                  onValueChange={(val) => handleLanguageChange(val)}
                >
                  <SelectTrigger className="w-full h-[50px] rounded-full border border-[#26317F]/30 bg-white px-4 text-sm sm:text-base text-slate-900 shadow-sm flex items-center justify-between">
                    <SelectValue />
                    <ChevronDown className="ml-auto h-4 w-4 opacity-50" />
                  </SelectTrigger>
                  <SelectContent className="rounded-[20px] bg-white border border-slate-100 shadow-xl p-2 z-[100]">
                    {languagesList.map((l) => (
                      <SelectItem
                        key={l.code}
                        value={l.code}
                        className="cursor-pointer py-2 px-4 rounded-xl hover:bg-slate-50 flex items-center justify-between"
                      >
                        {l.name}
                        {l.name === languagePreference && (
                          <Check className="w-4 h-4" />
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleCompleteConfiguration}
              className="w-full mt-2 bg-[#3E3EFF] hover:bg-[#3232CC] text-white font-bold py-3 sm:py-3.5 px-4 sm:px-6 rounded-full shadow-lg shadow-[#3838E9]/20 transition-all active:scale-[0.98] flex items-center justify-center gap-2 sm:gap-3 text-sm sm:text-base"
            >
              {loadingCompleteConfiguration ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <div className="flex items-center justify-center gap-2">
                  {dictionary["complete-configuration"][language]}
                  <ChevronDown className="rotate-[-90deg] w-4 h-4" />
                </div>
              )}
            </button>

            {/* Warning Footer */}
            <div className="absolute top-full left-0 w-full flex justify-center mt-4 sm:mt-6">
              <div className="flex items-center gap-1.5 sm:gap-2 text-[#26317F]">
                <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="text-[10px] sm:text-xs font-medium text-center">
                  {
                    dictionary["these-settings-cannot-be-changed-later"][
                      language
                    ]
                  }
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
